import { createServerFn } from '@tanstack/react-start';
import { Functions } from 'node-appwrite';
import { and, asc, count, desc, eq, gte, ilike, inArray, isNotNull, isNull, lt, ne, or, sql } from 'drizzle-orm';
import { alias } from 'drizzle-orm/pg-core';
import { z } from 'zod';

import { getDb } from '@/db/client';
import {
    sportArticleRefs,
    sportClubs,
    sportCompetitions,
    sportDisciplines,
    sportEditions,
    sportFixtures,
    sportMatchEvents,
    sportMatchLineups,
    sportPeople,
    sportPhaseGroups,
    sportPhaseParticipations,
    sportPhases,
    sportPlayerSeasonStats,
    sportSeasons,
    sportSquadMemberships,
    sportStandings,
    sportTeams,
    sportTies,
    sportVenues,
} from '@/db/schema';
import { generateHomeAwayFixtures, scoreFromEvents } from '@/lib/sport/fixtures';
import { FOOTBALL_DISCIPLINE_KEY, isScoringEvent, MATCH_EVENT_TYPES } from '@/lib/sport/football';
import { personName } from '@/lib/sport/names';
import {
    DEFAULT_LEAGUE_RULES,
    mergePhaseRules,
    parsePhaseRules,
    phaseRulesSchema,
    PHASE_KINDS,
    rulesForPhaseKind,
} from '@/lib/sport/rules';
import { slugifySportName, uniqueSlug } from '@/lib/sport/slugs';
import { statusAfterSave } from '@/lib/sport/coverage';
import { requireAdminAccess } from '@/server/functions/auth';
import { createAdminClient } from '@/server/lib/appwrite';
import {
    rebuildAfterFixture,
    rebuildAllActiveAggregates,
    rebuildPhaseStandings,
} from '@/server/sport/rebuild';

function newId() {
    return crypto.randomUUID();
}

function emptyToNull(v: string | null | undefined) {
    if (v === undefined || v === null) return null;
    const t = v.trim();
    return t === '' ? null : t;
}

const listPagingSchema = {
    q: z.string().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
};

function listPaging(data: { limit?: number; offset?: number }) {
    return { limit: data.limit ?? 50, offset: data.offset ?? 0 };
}

function likePattern(q?: string) {
    const t = q?.trim();
    return t ? `%${t}%` : null;
}

function uniqueIds(ids: Array<string | null | undefined>) {
    return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

async function ensureFootballDiscipline() {
    const db = getDb();
    const [existing] = await db
        .select()
        .from(sportDisciplines)
        .where(eq(sportDisciplines.key, FOOTBALL_DISCIPLINE_KEY))
        .limit(1);
    if (existing) return existing;
    const now = new Date();
    const row = {
        id: newId(),
        key: FOOTBALL_DISCIPLINE_KEY,
        name: 'כדורגל',
        config: {},
        createdAt: now,
        updatedAt: now,
    };
    await db.insert(sportDisciplines).values(row);
    return row;
}

async function ensureClubFirstTeam(clubId: string, clubName: string) {
    const db = getDb();
    const [existing] = await db
        .select()
        .from(sportTeams)
        .where(and(eq(sportTeams.clubId, clubId), eq(sportTeams.kind, 'first')))
        .limit(1);
    if (existing) return existing;
    const now = new Date();
    const row = {
        id: newId(),
        clubId,
        name: clubName,
        kind: 'first' as const,
        createdAt: now,
        updatedAt: now,
    };
    await db.insert(sportTeams).values(row);
    return row;
}

async function uniqueClubSlug(base: string, exceptId?: string) {
    const rows = await getDb().select({ slug: sportClubs.slug, id: sportClubs.id }).from(sportClubs);
    const taken = new Set(rows.filter((r) => r.id !== exceptId).map((r) => r.slug));
    return uniqueSlug(base, taken);
}

async function uniquePersonSlug(base: string, exceptId?: string) {
    const rows = await getDb().select({ slug: sportPeople.slug, id: sportPeople.id }).from(sportPeople);
    const taken = new Set(rows.filter((r) => r.id !== exceptId).map((r) => r.slug));
    return uniqueSlug(base, taken);
}

async function uniqueVenueSlug(base: string, exceptId?: string) {
    const rows = await getDb().select({ slug: sportVenues.slug, id: sportVenues.id }).from(sportVenues);
    const taken = new Set(rows.filter((r) => r.id !== exceptId).map((r) => r.slug));
    return uniqueSlug(base, taken);
}

async function uniqueCompetitionSlug(base: string, exceptId?: string) {
    const rows = await getDb()
        .select({ slug: sportCompetitions.slug, id: sportCompetitions.id })
        .from(sportCompetitions);
    const taken = new Set(rows.filter((r) => r.id !== exceptId).map((r) => r.slug));
    return uniqueSlug(base, taken);
}

async function uniqueSeasonSlug(base: string, exceptId?: string) {
    const rows = await getDb().select({ slug: sportSeasons.slug, id: sportSeasons.id }).from(sportSeasons);
    const taken = new Set(rows.filter((r) => r.id !== exceptId).map((r) => r.slug));
    return uniqueSlug(base, taken);
}

const hubHomeTeam = alias(sportTeams, 'hub_home_team');
const hubAwayTeam = alias(sportTeams, 'hub_away_team');
const hubHomeClub = alias(sportClubs, 'hub_home_club');
const hubAwayClub = alias(sportClubs, 'hub_away_club');

function countByStatus(rows: Array<{ status: string; value: number }>) {
    const map: Record<string, number> = {};
    for (const row of rows) map[row.status] = Number(row.value ?? 0);
    return map;
}

function mapHubFixture(row: {
    id: string;
    status: string;
    coverageMode: string;
    round: number;
    minute: number | null;
    period: string | null;
    kickoffAt: Date | null;
    homeGoals: number | null;
    awayGoals: number | null;
    editionId: string;
    competitionName: string;
    seasonLabel: string;
    homeName: string;
    homeShort: string | null;
    awayName: string;
    awayShort: string | null;
}) {
    return {
        id: row.id,
        editionId: row.editionId,
        status: row.status,
        coverageMode: row.coverageMode,
        round: row.round,
        minute: row.minute,
        period: row.period,
        kickoffAt: row.kickoffAt,
        homeGoals: row.homeGoals,
        awayGoals: row.awayGoals,
        homeName: row.homeShort || row.homeName,
        awayName: row.awayShort || row.awayName,
        competitionName: row.competitionName,
        seasonLabel: row.seasonLabel,
    };
}

const hubFixtureSelect = {
    id: sportFixtures.id,
    status: sportFixtures.status,
    coverageMode: sportFixtures.coverageMode,
    round: sportFixtures.round,
    minute: sportFixtures.minute,
    period: sportFixtures.period,
    kickoffAt: sportFixtures.kickoffAt,
    homeGoals: sportFixtures.homeGoals,
    awayGoals: sportFixtures.awayGoals,
    editionId: sportEditions.id,
    competitionName: sportCompetitions.name,
    seasonLabel: sportSeasons.label,
    homeName: hubHomeClub.name,
    homeShort: hubHomeClub.shortName,
    awayName: hubAwayClub.name,
    awayShort: hubAwayClub.shortName,
};

function hubFixtureQuery() {
    return getDb()
        .select(hubFixtureSelect)
        .from(sportFixtures)
        .innerJoin(sportPhases, eq(sportFixtures.phaseId, sportPhases.id))
        .innerJoin(sportEditions, eq(sportPhases.editionId, sportEditions.id))
        .innerJoin(sportCompetitions, eq(sportEditions.competitionId, sportCompetitions.id))
        .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
        .innerJoin(hubHomeTeam, eq(sportFixtures.homeTeamId, hubHomeTeam.id))
        .innerJoin(hubHomeClub, eq(hubHomeTeam.clubId, hubHomeClub.id))
        .innerJoin(hubAwayTeam, eq(sportFixtures.awayTeamId, hubAwayTeam.id))
        .innerJoin(hubAwayClub, eq(hubAwayTeam.clubId, hubAwayClub.id));
}

export const adminSearchSportFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        kind: z.enum(['people', 'clubs', 'venues', 'seasons']),
        q: z.string().optional(),
        ids: z.array(z.string().min(1)).max(20).optional(),
        limit: z.number().int().min(1).max(40).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 20;
        const pattern = likePattern(data.q);
        const requestedIds = uniqueIds(data.ids ?? []);

        if (data.kind === 'people') {
            const where = pattern
                ? or(
                    ilike(sportPeople.firstName, pattern),
                    ilike(sportPeople.lastName, pattern),
                    ilike(sportPeople.slug, pattern),
                    ilike(sql<string>`(${sportPeople.firstName} || ' ' || ${sportPeople.lastName})`, pattern),
                    ilike(sportPeople.id, pattern),
                )
                : undefined;
            const rows = await db
                .select({
                    id: sportPeople.id,
                    firstName: sportPeople.firstName,
                    lastName: sportPeople.lastName,
                })
                .from(sportPeople)
                .where(where)
                .orderBy(asc(sportPeople.lastName), asc(sportPeople.firstName))
                .limit(limit);
            const missing = requestedIds.filter((id) => !rows.some((r) => r.id === id));
            const extra = missing.length
                ? await db
                    .select({
                        id: sportPeople.id,
                        firstName: sportPeople.firstName,
                        lastName: sportPeople.lastName,
                    })
                    .from(sportPeople)
                    .where(inArray(sportPeople.id, missing))
                : [];
            const seen = new Set<string>();
            const options: { value: string; label: string }[] = [];
            for (const row of [...extra, ...rows]) {
                if (seen.has(row.id)) continue;
                seen.add(row.id);
                options.push({ value: row.id, label: personName(row) });
            }
            return { options };
        }

        if (data.kind === 'clubs') {
            const where = pattern
                ? or(
                    ilike(sportClubs.name, pattern),
                    ilike(sportClubs.shortName, pattern),
                    ilike(sportClubs.slug, pattern),
                    ilike(sportClubs.city, pattern),
                    ilike(sportClubs.id, pattern),
                )
                : undefined;
            const rows = await db
                .select({
                    id: sportClubs.id,
                    name: sportClubs.name,
                    shortName: sportClubs.shortName,
                    city: sportClubs.city,
                })
                .from(sportClubs)
                .where(where)
                .orderBy(asc(sportClubs.name))
                .limit(limit);
            const missing = requestedIds.filter((id) => !rows.some((r) => r.id === id));
            const extra = missing.length
                ? await db
                    .select({
                        id: sportClubs.id,
                        name: sportClubs.name,
                        shortName: sportClubs.shortName,
                        city: sportClubs.city,
                    })
                    .from(sportClubs)
                    .where(inArray(sportClubs.id, missing))
                : [];
            const seen = new Set<string>();
            const options: { value: string; label: string }[] = [];
            for (const row of [...extra, ...rows]) {
                if (seen.has(row.id)) continue;
                seen.add(row.id);
                const city = row.city?.trim();
                options.push({
                    value: row.id,
                    label: city ? `${row.shortName || row.name} · ${city}` : (row.shortName || row.name),
                });
            }
            return { options };
        }

        if (data.kind === 'venues') {
            const where = pattern
                ? or(
                    ilike(sportVenues.name, pattern),
                    ilike(sportVenues.slug, pattern),
                    ilike(sportVenues.city, pattern),
                    ilike(sportVenues.id, pattern),
                )
                : undefined;
            const rows = await db
                .select({
                    id: sportVenues.id,
                    name: sportVenues.name,
                    city: sportVenues.city,
                })
                .from(sportVenues)
                .where(where)
                .orderBy(asc(sportVenues.name))
                .limit(limit);
            const missing = requestedIds.filter((id) => !rows.some((r) => r.id === id));
            const extra = missing.length
                ? await db
                    .select({
                        id: sportVenues.id,
                        name: sportVenues.name,
                        city: sportVenues.city,
                    })
                    .from(sportVenues)
                    .where(inArray(sportVenues.id, missing))
                : [];
            const seen = new Set<string>();
            const options: { value: string; label: string }[] = [];
            for (const row of [...extra, ...rows]) {
                if (seen.has(row.id)) continue;
                seen.add(row.id);
                options.push({
                    value: row.id,
                    label: row.city ? `${row.name} · ${row.city}` : row.name,
                });
            }
            return { options };
        }

        const where = pattern
            ? or(
                ilike(sportSeasons.label, pattern),
                ilike(sportSeasons.slug, pattern),
                ilike(sportSeasons.id, pattern),
            )
            : undefined;
        const rows = await db
            .select({
                id: sportSeasons.id,
                label: sportSeasons.label,
            })
            .from(sportSeasons)
            .where(where)
            .orderBy(desc(sportSeasons.label))
            .limit(limit);
        const missing = requestedIds.filter((id) => !rows.some((r) => r.id === id));
        const extra = missing.length
            ? await db
                .select({
                    id: sportSeasons.id,
                    label: sportSeasons.label,
                })
                .from(sportSeasons)
                .where(inArray(sportSeasons.id, missing))
            : [];
        const seen = new Set<string>();
        const options: { value: string; label: string }[] = [];
        for (const row of [...extra, ...rows]) {
            if (seen.has(row.id)) continue;
            seen.add(row.id);
            options.push({ value: row.id, label: row.label });
        }
        return { options };
    });

export const adminListClubTeamsFn = createServerFn({ method: 'GET' })
    .validator(z.object({ clubId: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const rows = await getDb()
            .select({
                id: sportTeams.id,
                name: sportTeams.name,
                kind: sportTeams.kind,
            })
            .from(sportTeams)
            .where(eq(sportTeams.clubId, data.clubId))
            .orderBy(asc(sportTeams.kind), asc(sportTeams.name));
        return { rows };
    });

export const adminSportHubFn = createServerFn({ method: 'GET' }).handler(async () => {
    await requireAdminAccess();
    const discipline = await ensureFootballDiscipline();
    const db = getDb();
    const now = new Date();

    const [
        competitionCount,
        clubCount,
        peopleCount,
        venueCount,
        editionCount,
        seasonStatusRows,
        fixtureStatusRows,
        liveRows,
        upcomingRows,
        recentRows,
        postponedRows,
        staleRows,
        noKickoffRows,
        activeEditions,
    ] = await Promise.all([
        db.select({ value: count() }).from(sportCompetitions).where(eq(sportCompetitions.disciplineId, discipline.id)),
        db.select({ value: count() }).from(sportClubs),
        db.select({ value: count() }).from(sportPeople),
        db.select({ value: count() }).from(sportVenues),
        db.select({ value: count() }).from(sportEditions),
        db.select({ status: sportSeasons.status, value: count() }).from(sportSeasons).groupBy(sportSeasons.status),
        db.select({ status: sportFixtures.status, value: count() }).from(sportFixtures).groupBy(sportFixtures.status),
        hubFixtureQuery().where(eq(sportFixtures.status, 'live')).orderBy(asc(sportFixtures.kickoffAt)).limit(12),
        hubFixtureQuery()
            .where(and(eq(sportFixtures.status, 'scheduled'), gte(sportFixtures.kickoffAt, now)))
            .orderBy(asc(sportFixtures.kickoffAt))
            .limit(10),
        hubFixtureQuery()
            .where(eq(sportFixtures.status, 'played'))
            .orderBy(desc(sportFixtures.updatedAt))
            .limit(8),
        hubFixtureQuery()
            .where(eq(sportFixtures.status, 'postponed'))
            .orderBy(desc(sportFixtures.updatedAt))
            .limit(8),
        hubFixtureQuery()
            .where(and(eq(sportFixtures.status, 'scheduled'), lt(sportFixtures.kickoffAt, now)))
            .orderBy(asc(sportFixtures.kickoffAt))
            .limit(8),
        hubFixtureQuery()
            .where(and(eq(sportFixtures.status, 'scheduled'), isNull(sportFixtures.kickoffAt)))
            .orderBy(desc(sportFixtures.updatedAt))
            .limit(6),
        db
            .select({
                editionId: sportEditions.id,
                competitionId: sportCompetitions.id,
                competitionName: sportCompetitions.name,
                type: sportCompetitions.type,
                featured: sportCompetitions.featured,
                seasonId: sportSeasons.id,
                seasonLabel: sportSeasons.label,
                status: sportSeasons.status,
            })
            .from(sportEditions)
            .innerJoin(sportCompetitions, eq(sportEditions.competitionId, sportCompetitions.id))
            .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
            .where(eq(sportSeasons.status, 'active'))
            .orderBy(desc(sportCompetitions.featured), asc(sportCompetitions.name))
            .limit(12),
    ]);

    const seasonsByStatus = countByStatus(seasonStatusRows);
    const fixturesByStatus = countByStatus(fixtureStatusRows);
    const seasons =
        (seasonsByStatus.upcoming ?? 0) +
        (seasonsByStatus.active ?? 0) +
        (seasonsByStatus.completed ?? 0);

    const attention = [
        ...staleRows.map((row) => ({ kind: 'stale' as const, fixture: mapHubFixture(row) })),
        ...postponedRows.map((row) => ({ kind: 'postponed' as const, fixture: mapHubFixture(row) })),
        ...noKickoffRows.map((row) => ({ kind: 'no-kickoff' as const, fixture: mapHubFixture(row) })),
    ].slice(0, 12);

    return {
        counts: {
            competitions: competitionCount[0]?.value ?? 0,
            seasons,
            seasonsActive: seasonsByStatus.active ?? 0,
            seasonsUpcoming: seasonsByStatus.upcoming ?? 0,
            seasonsCompleted: seasonsByStatus.completed ?? 0,
            clubs: clubCount[0]?.value ?? 0,
            people: peopleCount[0]?.value ?? 0,
            venues: venueCount[0]?.value ?? 0,
            editions: editionCount[0]?.value ?? 0,
            live: fixturesByStatus.live ?? 0,
            scheduled: fixturesByStatus.scheduled ?? 0,
            played: fixturesByStatus.played ?? 0,
            postponed: fixturesByStatus.postponed ?? 0,
        },
        live: liveRows.map(mapHubFixture),
        upcoming: upcomingRows.map(mapHubFixture),
        recent: recentRows.map(mapHubFixture),
        attention,
        activeEditions,
    };
});

export const adminListCompetitionsFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        ...listPagingSchema,
        type: z.enum(['all', 'league', 'cup', 'supercup', 'playoff']).optional(),
        featured: z.enum(['all', 'yes', 'no']).optional(),
        seasonStatus: z.enum(['all', 'upcoming', 'active', 'completed']).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const discipline = await ensureFootballDiscipline();
        const db = getDb();
        const { limit, offset } = listPaging(data);
        const filters = [eq(sportCompetitions.disciplineId, discipline.id)];
        if (data.type && data.type !== 'all') {
            filters.push(eq(sportCompetitions.type, data.type));
        }
        if (data.featured === 'yes') filters.push(eq(sportCompetitions.featured, true));
        if (data.featured === 'no') filters.push(eq(sportCompetitions.featured, false));
        if (data.seasonStatus && data.seasonStatus !== 'all') {
            const seasonRows = await db
                .select({ id: sportEditions.competitionId })
                .from(sportEditions)
                .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
                .where(eq(sportSeasons.status, data.seasonStatus));
            const ids = [...new Set(seasonRows.map((r) => r.id))];
            if (ids.length === 0) filters.push(sql`false`);
            else filters.push(inArray(sportCompetitions.id, ids));
        }
        const pattern = likePattern(data.q);
        if (pattern) {
            filters.push(or(
                ilike(sportCompetitions.name, pattern),
                ilike(sportCompetitions.slug, pattern),
                ilike(sportCompetitions.country, pattern),
                ilike(sportCompetitions.id, pattern),
            )!);
        }
        const where = and(...filters);
        const [rows, totalRow] = await Promise.all([
            db.select().from(sportCompetitions).where(where).orderBy(asc(sportCompetitions.name)).limit(limit).offset(offset),
            db.select({ value: count() }).from(sportCompetitions).where(where),
        ]);
        const ids = rows.map((r) => r.id);
        const seasons = ids.length
            ? await db
                .select({
                    id: sportEditions.id,
                    competitionId: sportEditions.competitionId,
                    seasonId: sportSeasons.id,
                    label: sportSeasons.label,
                    slug: sportSeasons.slug,
                    status: sportSeasons.status,
                })
                .from(sportEditions)
                .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
                .where(inArray(sportEditions.competitionId, ids))
                .orderBy(desc(sportSeasons.label))
            : [];
        return { rows, seasons, total: totalRow[0]?.value ?? 0 };
    });

export const adminListVenuesFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        ...listPagingSchema,
        city: z.string().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const { limit, offset } = listPaging(data);
        const filters = [];
        if (data.city && data.city !== 'all') filters.push(eq(sportVenues.city, data.city));
        const pattern = likePattern(data.q);
        if (pattern) {
            filters.push(or(
                ilike(sportVenues.name, pattern),
                ilike(sportVenues.slug, pattern),
                ilike(sportVenues.city, pattern),
                ilike(sportVenues.notes, pattern),
                ilike(sportVenues.id, pattern),
            )!);
        }
        const where = filters.length ? and(...filters) : undefined;
        const [rows, totalRow, cityRows] = await Promise.all([
            db.select().from(sportVenues).where(where).orderBy(asc(sportVenues.name)).limit(limit).offset(offset),
            db.select({ value: count() }).from(sportVenues).where(where),
            db.selectDistinct({ city: sportVenues.city }).from(sportVenues)
                .where(and(isNotNull(sportVenues.city), ne(sportVenues.city, '')))
                .orderBy(asc(sportVenues.city)),
        ]);
        return {
            rows,
            total: totalRow[0]?.value ?? 0,
            cities: cityRows.map((r) => r.city).filter((c): c is string => Boolean(c)),
        };
    });

export const adminGetVenueFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb().select().from(sportVenues).where(eq(sportVenues.id, data.id)).limit(1);
        return row ?? null;
    });

export const adminUpsertVenueFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        name: z.string().min(1),
        slug: z.string().optional(),
        city: z.string().nullable().optional(),
        capacity: z.number().int().nullable().optional(),
        notes: z.string().nullable().optional(),
        photoFileId: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const slug = await uniqueVenueSlug(data.slug || data.name, data.id);
        const values = {
            id,
            name: data.name.trim(),
            slug,
            city: emptyToNull(data.city),
            capacity: data.capacity ?? null,
            notes: emptyToNull(data.notes),
            photoFileId: emptyToNull(data.photoFileId),
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportVenues.id }).from(sportVenues).where(eq(sportVenues.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportVenues).set(values).where(eq(sportVenues.id, id));
        else await db.insert(sportVenues).values({ ...values, createdAt: now });
        return { id };
    });

export const adminDeleteVenueFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(sportVenues).where(eq(sportVenues.id, data.id));
        return { ok: true };
    });

export const adminListClubsFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        ...listPagingSchema,
        city: z.string().optional(),
        hasVenue: z.enum(['all', 'yes', 'no']).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const { limit, offset } = listPaging(data);
        const filters = [];
        if (data.city && data.city !== 'all') filters.push(eq(sportClubs.city, data.city));
        if (data.hasVenue === 'yes') filters.push(isNotNull(sportClubs.venueId));
        if (data.hasVenue === 'no') filters.push(isNull(sportClubs.venueId));
        const pattern = likePattern(data.q);
        if (pattern) {
            filters.push(or(
                ilike(sportClubs.name, pattern),
                ilike(sportClubs.shortName, pattern),
                ilike(sportClubs.slug, pattern),
                ilike(sportClubs.city, pattern),
                ilike(sportClubs.id, pattern),
            )!);
        }
        const where = filters.length ? and(...filters) : undefined;
        const [rows, totalRow, cityRows] = await Promise.all([
            db.select().from(sportClubs).where(where).orderBy(asc(sportClubs.name)).limit(limit).offset(offset),
            db.select({ value: count() }).from(sportClubs).where(where),
            db.selectDistinct({ city: sportClubs.city }).from(sportClubs)
                .where(and(isNotNull(sportClubs.city), ne(sportClubs.city, '')))
                .orderBy(asc(sportClubs.city)),
        ]);
        return {
            rows,
            total: totalRow[0]?.value ?? 0,
            cities: cityRows.map((r) => r.city).filter((c): c is string => Boolean(c)),
        };
    });

export const adminGetClubFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [club] = await db.select().from(sportClubs).where(eq(sportClubs.id, data.id)).limit(1);
        if (!club) return null;
        const teams = await db.select().from(sportTeams).where(eq(sportTeams.clubId, club.id)).orderBy(asc(sportTeams.kind), asc(sportTeams.name));
        const teamIds = teams.map((t) => t.id);
        const venue = club.venueId
            ? (await db.select().from(sportVenues).where(eq(sportVenues.id, club.venueId)).limit(1))[0] ?? null
            : null;
        const memberships = teamIds.length
            ? await db.select().from(sportSquadMemberships).where(inArray(sportSquadMemberships.teamId, teamIds))
            : [];
        const personIds = [...new Set(memberships.map((m) => m.personId))];
        const people = personIds.length
            ? await db.select().from(sportPeople).where(inArray(sportPeople.id, personIds))
            : [];
        const participations = teamIds.length
            ? await db.select().from(sportPhaseParticipations).where(inArray(sportPhaseParticipations.teamId, teamIds))
            : [];
        const phaseIds = [...new Set(participations.map((p) => p.phaseId))];
        const phases = phaseIds.length
            ? await db.select().from(sportPhases).where(inArray(sportPhases.id, phaseIds))
            : [];
        const editionIds = [...new Set(phases.map((p) => p.editionId))];
        const editions = editionIds.length
            ? await db.select().from(sportEditions).where(inArray(sportEditions.id, editionIds))
            : [];
        const seasonIds = [...new Set(editions.map((e) => e.seasonId))];
        const seasons = seasonIds.length
            ? await db.select().from(sportSeasons).where(inArray(sportSeasons.id, seasonIds))
            : [];
        const competitionIds = [...new Set(editions.map((e) => e.competitionId))];
        const competitions = competitionIds.length
            ? await db.select().from(sportCompetitions).where(inArray(sportCompetitions.id, competitionIds))
            : [];
        const tournaments = participations.flatMap((part) => {
            const phase = phases.find((p) => p.id === part.phaseId);
            const edition = phase ? editions.find((e) => e.id === phase.editionId) : undefined;
            const season = edition ? seasons.find((s) => s.id === edition.seasonId) : undefined;
            const competition = edition ? competitions.find((c) => c.id === edition.competitionId) : undefined;
            const team = teams.find((t) => t.id === part.teamId);
            if (!phase || !edition || !season || !competition || !team) return [];
            if (season.status !== 'active' && season.status !== 'upcoming') return [];
            return [{
                participationId: part.id,
                team,
                phase,
                season,
                edition,
                competition,
            }];
        });
        const fixtureWhere = teamIds.length
            ? or(inArray(sportFixtures.homeTeamId, teamIds), inArray(sportFixtures.awayTeamId, teamIds))
            : undefined;
        const fixtures = fixtureWhere
            ? await db.select().from(sportFixtures).where(fixtureWhere).orderBy(desc(sportFixtures.kickoffAt), desc(sportFixtures.round)).limit(80)
            : [];
        const fixtureTeamIds = uniqueIds(fixtures.flatMap((f) => [f.homeTeamId, f.awayTeamId]));
        const extraTeamIds = fixtureTeamIds.filter((id) => !teamIds.includes(id));
        const opponentTeams = extraTeamIds.length
            ? await db.select().from(sportTeams).where(inArray(sportTeams.id, extraTeamIds))
            : [];
        const opponentClubIds = uniqueIds(opponentTeams.map((t) => t.clubId));
        const opponentClubs = opponentClubIds.length
            ? await db.select().from(sportClubs).where(inArray(sportClubs.id, opponentClubIds))
            : [];
        const fixturePhaseIds = uniqueIds(fixtures.map((f) => f.phaseId));
        const fixturePhases = fixturePhaseIds.length
            ? await db.select().from(sportPhases).where(inArray(sportPhases.id, fixturePhaseIds))
            : [];
        const fixtureEditionIds = uniqueIds(fixturePhases.map((p) => p.editionId));
        const fixtureEditions = fixtureEditionIds.length
            ? await db.select().from(sportEditions).where(inArray(sportEditions.id, fixtureEditionIds))
            : [];
        const extraSeasonIds = uniqueIds(fixtureEditions.map((e) => e.seasonId)).filter((id) => !seasons.some((s) => s.id === id));
        const extraCompetitionIds = uniqueIds(fixtureEditions.map((e) => e.competitionId)).filter((id) => !competitions.some((c) => c.id === id));
        const extraSeasons = extraSeasonIds.length
            ? await db.select().from(sportSeasons).where(inArray(sportSeasons.id, extraSeasonIds))
            : [];
        const extraCompetitions = extraCompetitionIds.length
            ? await db.select().from(sportCompetitions).where(inArray(sportCompetitions.id, extraCompetitionIds))
            : [];
        const allSeasons = [...seasons, ...extraSeasons];
        const allCompetitions = [...competitions, ...extraCompetitions];
        const fixtureRows = fixtures.map((fixture) => {
            const phase = fixturePhases.find((p) => p.id === fixture.phaseId);
            const edition = phase ? fixtureEditions.find((e) => e.id === phase.editionId) : undefined;
            const season = edition ? allSeasons.find((s) => s.id === edition.seasonId) : undefined;
            const competition = edition ? allCompetitions.find((c) => c.id === edition.competitionId) : undefined;
            return { fixture, phase: phase ?? null, edition: edition ?? null, season: season ?? null, competition: competition ?? null };
        });
        return {
            club,
            teams,
            venue,
            memberships,
            people,
            tournaments,
            fixtures: fixtureRows,
            fixtureTeams: [...teams, ...opponentTeams],
            fixtureClubs: [club, ...opponentClubs],
        };
    });

export const adminUpsertClubFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        name: z.string().min(1),
        shortName: z.string().nullable().optional(),
        slug: z.string().optional(),
        city: z.string().nullable().optional(),
        foundedYear: z.number().int().nullable().optional(),
        venueId: z.string().nullable().optional(),
        crestFileId: z.string().nullable().optional(),
        crestMonoFileId: z.string().nullable().optional(),
        primaryColor: z.string().nullable().optional(),
        secondaryColor: z.string().nullable().optional(),
        tertiaryColor: z.string().nullable().optional(),
        externalId: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const discipline = await ensureFootballDiscipline();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const slug = await uniqueClubSlug(data.slug || data.name, data.id);
        const values = {
            id,
            disciplineId: discipline.id,
            name: data.name.trim(),
            shortName: emptyToNull(data.shortName),
            slug,
            city: emptyToNull(data.city),
            foundedYear: data.foundedYear ?? null,
            venueId: emptyToNull(data.venueId),
            crestFileId: emptyToNull(data.crestFileId),
            crestMonoFileId: emptyToNull(data.crestMonoFileId),
            primaryColor: emptyToNull(data.primaryColor),
            secondaryColor: emptyToNull(data.secondaryColor),
            tertiaryColor: emptyToNull(data.tertiaryColor),
            externalId: emptyToNull(data.externalId),
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportClubs.id }).from(sportClubs).where(eq(sportClubs.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportClubs).set(values).where(eq(sportClubs.id, id));
        else await db.insert(sportClubs).values({ ...values, createdAt: now });
        await ensureClubFirstTeam(id, data.name.trim());
        return { id };
    });

export const adminDeleteClubFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        await db.delete(sportTeams).where(eq(sportTeams.clubId, data.id));
        await db.delete(sportClubs).where(eq(sportClubs.id, data.id));
        return { ok: true };
    });

const TEAM_KINDS = ['first', 'youth', 'women'] as const;

export const adminUpsertTeamFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        clubId: z.string().min(1),
        name: z.string().min(1),
        kind: z.enum(TEAM_KINDS),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const [club] = await db.select({ id: sportClubs.id }).from(sportClubs).where(eq(sportClubs.id, data.clubId)).limit(1);
        if (!club) throw new Error('מועדון לא נמצא');
        const [existing] = data.id
            ? await db.select().from(sportTeams).where(eq(sportTeams.id, id)).limit(1)
            : [];
        if (existing) {
            if (existing.clubId !== data.clubId) throw new Error('הקבוצה שייכת למועדון אחר');
            await db.update(sportTeams).set({
                name: data.name.trim(),
                kind: data.kind,
                updatedAt: now,
            }).where(eq(sportTeams.id, id));
        } else {
            await db.insert(sportTeams).values({
                id,
                clubId: data.clubId,
                name: data.name.trim(),
                kind: data.kind,
                createdAt: now,
                updatedAt: now,
            });
        }
        return { id };
    });

export const adminDeleteTeamFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [team] = await db.select().from(sportTeams).where(eq(sportTeams.id, data.id)).limit(1);
        if (!team) return { ok: true };
        const siblings = await db.select({ id: sportTeams.id }).from(sportTeams).where(eq(sportTeams.clubId, team.clubId));
        if (siblings.length <= 1) throw new Error('לא ניתן למחוק את הקבוצה האחרונה של המועדון');
        const [fixture] = await db.select({ id: sportFixtures.id }).from(sportFixtures).where(
            or(eq(sportFixtures.homeTeamId, data.id), eq(sportFixtures.awayTeamId, data.id)),
        ).limit(1);
        if (fixture) throw new Error('לא ניתן למחוק קבוצה עם משחקים. הסירו אותה מהשלבים קודם.');
        await db.delete(sportPhaseParticipations).where(eq(sportPhaseParticipations.teamId, data.id));
        await db.delete(sportSquadMemberships).where(eq(sportSquadMemberships.teamId, data.id));
        await db.delete(sportStandings).where(eq(sportStandings.teamId, data.id));
        await db.delete(sportTeams).where(eq(sportTeams.id, data.id));
        return { ok: true };
    });

export const adminListPeopleFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        ...listPagingSchema,
        nationality: z.string().optional(),
        preferredFoot: z.string().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const { limit, offset } = listPaging(data);
        const filters = [];
        if (data.nationality && data.nationality !== 'all') {
            filters.push(eq(sportPeople.nationality, data.nationality));
        }
        if (data.preferredFoot && data.preferredFoot !== 'all') {
            filters.push(eq(sportPeople.preferredFoot, data.preferredFoot));
        }
        const pattern = likePattern(data.q);
        if (pattern) {
            filters.push(or(
                ilike(sportPeople.firstName, pattern),
                ilike(sportPeople.lastName, pattern),
                ilike(sportPeople.slug, pattern),
                ilike(sportPeople.nationality, pattern),
                ilike(sportPeople.id, pattern),
            )!);
        }
        const where = filters.length ? and(...filters) : undefined;
        const [rows, totalRow, nationalityRows, footRows] = await Promise.all([
            db.select().from(sportPeople).where(where).orderBy(asc(sportPeople.lastName), asc(sportPeople.firstName)).limit(limit).offset(offset),
            db.select({ value: count() }).from(sportPeople).where(where),
            db.selectDistinct({ nationality: sportPeople.nationality }).from(sportPeople)
                .where(and(isNotNull(sportPeople.nationality), ne(sportPeople.nationality, '')))
                .orderBy(asc(sportPeople.nationality)),
            db.selectDistinct({ preferredFoot: sportPeople.preferredFoot }).from(sportPeople)
                .where(and(isNotNull(sportPeople.preferredFoot), ne(sportPeople.preferredFoot, '')))
                .orderBy(asc(sportPeople.preferredFoot)),
        ]);
        return {
            rows,
            total: totalRow[0]?.value ?? 0,
            nationalities: nationalityRows.map((r) => r.nationality).filter((v): v is string => Boolean(v)),
            preferredFeet: footRows.map((r) => r.preferredFoot).filter((v): v is string => Boolean(v)),
        };
    });

export const adminGetPersonFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [person] = await db.select().from(sportPeople).where(eq(sportPeople.id, data.id)).limit(1);
        if (!person) return null;
        const memberships = await db.select().from(sportSquadMemberships).where(eq(sportSquadMemberships.personId, person.id));
        const teamIds = uniqueIds(memberships.map((m) => m.teamId));
        const seasonIds = uniqueIds(memberships.map((m) => m.seasonId));
        const teams = teamIds.length
            ? await db.select().from(sportTeams).where(inArray(sportTeams.id, teamIds))
            : [];
        const clubIds = uniqueIds(teams.map((t) => t.clubId));
        const [clubs, seasons] = await Promise.all([
            clubIds.length
                ? db.select().from(sportClubs).where(inArray(sportClubs.id, clubIds))
                : Promise.resolve([]),
            seasonIds.length
                ? db.select().from(sportSeasons).where(inArray(sportSeasons.id, seasonIds))
                : Promise.resolve([]),
        ]);
        return { person, clubs, memberships, teams, seasons };
    });

export const adminUpsertPersonFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        slug: z.string().optional(),
        dateOfBirth: z.string().nullable().optional(),
        nationality: z.string().nullable().optional(),
        heightCm: z.number().int().nullable().optional(),
        preferredFoot: z.string().nullable().optional(),
        position: z.string().nullable().optional(),
        photoFileId: z.string().nullable().optional(),
        externalId: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const slug = await uniquePersonSlug(
            data.slug || `${data.firstName} ${data.lastName}`,
            data.id,
        );
        const values = {
            id,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            slug,
            dateOfBirth: emptyToNull(data.dateOfBirth),
            nationality: emptyToNull(data.nationality),
            heightCm: data.heightCm ?? null,
            preferredFoot: emptyToNull(data.preferredFoot),
            position: emptyToNull(data.position),
            photoFileId: emptyToNull(data.photoFileId),
            externalId: emptyToNull(data.externalId),
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportPeople.id }).from(sportPeople).where(eq(sportPeople.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportPeople).set(values).where(eq(sportPeople.id, id));
        else await db.insert(sportPeople).values({ ...values, createdAt: now });
        return { id };
    });

export const adminDeletePersonFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(sportPeople).where(eq(sportPeople.id, data.id));
        return { ok: true };
    });

const competitionWrite = z.object({
    id: z.string().optional(),
    name: z.string().min(1),
    slug: z.string().optional(),
    type: z.enum(['league', 'cup', 'supercup', 'playoff']),
    country: z.string().nullable().optional(),
    confederation: z.string().nullable().optional(),
    tier: z.number().int().nullable().optional(),
    featured: z.boolean().optional(),
    logoFileId: z.string().nullable().optional(),
    defaultRules: phaseRulesSchema.optional(),
});

export const adminUpsertCompetitionFn = createServerFn({ method: 'POST' })
    .validator(competitionWrite)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const discipline = await ensureFootballDiscipline();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const slug = await uniqueCompetitionSlug(data.slug || data.name, data.id);
        const values = {
            id,
            disciplineId: discipline.id,
            name: data.name.trim(),
            slug,
            type: data.type,
            country: emptyToNull(data.country),
            confederation: emptyToNull(data.confederation),
            tier: data.tier ?? null,
            featured: data.featured ?? false,
            logoFileId: emptyToNull(data.logoFileId),
            defaultRules: data.defaultRules ?? DEFAULT_LEAGUE_RULES,
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportCompetitions.id }).from(sportCompetitions).where(eq(sportCompetitions.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportCompetitions).set(values).where(eq(sportCompetitions.id, id));
        else await db.insert(sportCompetitions).values({ ...values, createdAt: now });
        return { id };
    });

export const adminGetCompetitionFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [competition] = await db.select().from(sportCompetitions).where(eq(sportCompetitions.id, data.id)).limit(1);
        if (!competition) return null;
        const seasons = await db
            .select({
                id: sportEditions.id,
                seasonId: sportSeasons.id,
                label: sportSeasons.label,
                slug: sportSeasons.slug,
                status: sportSeasons.status,
            })
            .from(sportEditions)
            .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
            .where(eq(sportEditions.competitionId, competition.id))
            .orderBy(desc(sportSeasons.label));
        return { competition, seasons };
    });

export const adminDeleteCompetitionFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const editions = await db.select({ id: sportEditions.id }).from(sportEditions).where(eq(sportEditions.competitionId, data.id));
        await deletePhasesOfEditions(editions.map((e) => e.id));
        await db.delete(sportEditions).where(eq(sportEditions.competitionId, data.id));
        await db.delete(sportCompetitions).where(eq(sportCompetitions.id, data.id));
        return { ok: true };
    });

export const adminUpsertSeasonFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        label: z.string().min(1),
        slug: z.string().optional(),
        startsOn: z.string().nullable().optional(),
        endsOn: z.string().nullable().optional(),
        status: z.enum(['upcoming', 'active', 'completed']).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const slug = await uniqueSeasonSlug(data.slug || data.label, data.id);
        const values = {
            id,
            label: data.label.trim(),
            slug,
            startsOn: emptyToNull(data.startsOn),
            endsOn: emptyToNull(data.endsOn),
            status: data.status ?? 'upcoming',
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportSeasons.id }).from(sportSeasons).where(eq(sportSeasons.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportSeasons).set(values).where(eq(sportSeasons.id, id));
        else await db.insert(sportSeasons).values({ ...values, createdAt: now });
        return { id };
    });

export const adminListSeasonsFn = createServerFn({ method: 'GET' })
    .validator(z.object({
        ...listPagingSchema,
        status: z.enum(['all', 'upcoming', 'active', 'completed']).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const { limit, offset } = listPaging(data);
        const filters = [];
        if (data.status && data.status !== 'all') filters.push(eq(sportSeasons.status, data.status));
        const pattern = likePattern(data.q);
        if (pattern) {
            filters.push(or(
                ilike(sportSeasons.label, pattern),
                ilike(sportSeasons.slug, pattern),
                ilike(sportSeasons.id, pattern),
            )!);
        }
        const where = filters.length ? and(...filters) : undefined;
        const [rows, totalRow] = await Promise.all([
            db.select().from(sportSeasons).where(where).orderBy(desc(sportSeasons.label)).limit(limit).offset(offset),
            db.select({ value: count() }).from(sportSeasons).where(where),
        ]);
        const ids = rows.map((r) => r.id);
        const editions = ids.length
            ? await db
                .select({
                    id: sportEditions.id,
                    seasonId: sportEditions.seasonId,
                    competitionId: sportEditions.competitionId,
                    competitionName: sportCompetitions.name,
                })
                .from(sportEditions)
                .innerJoin(sportCompetitions, eq(sportEditions.competitionId, sportCompetitions.id))
                .where(inArray(sportEditions.seasonId, ids))
                .orderBy(asc(sportCompetitions.name))
            : [];
        return { rows, editions, total: totalRow[0]?.value ?? 0 };
    });

export const adminGetSeasonFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [season] = await db.select().from(sportSeasons).where(eq(sportSeasons.id, data.id)).limit(1);
        if (!season) return null;
        const editions = await db
            .select({
                id: sportEditions.id,
                competitionId: sportEditions.competitionId,
                competitionName: sportCompetitions.name,
            })
            .from(sportEditions)
            .innerJoin(sportCompetitions, eq(sportEditions.competitionId, sportCompetitions.id))
            .where(eq(sportEditions.seasonId, season.id))
            .orderBy(asc(sportCompetitions.name));
        return { season, editions };
    });

export const adminAttachEditionFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        competitionId: z.string().min(1),
        seasonId: z.string().optional(),
        label: z.string().optional(),
        status: z.enum(['upcoming', 'active', 'completed']).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        let seasonId = data.seasonId?.trim() || '';
        if (!seasonId) {
            const label = data.label?.trim();
            if (!label) throw new Error('בחרו עונה או הזינו תווית');
            const slug = slugifySportName(label);
            const [existingSeason] = await db.select().from(sportSeasons).where(eq(sportSeasons.slug, slug)).limit(1);
            if (existingSeason) {
                seasonId = existingSeason.id;
            } else {
                seasonId = newId();
                await db.insert(sportSeasons).values({
                    id: seasonId,
                    label,
                    slug,
                    status: data.status ?? 'active',
                    createdAt: now,
                    updatedAt: now,
                });
            }
        }
        const [existing] = await db.select({ id: sportEditions.id }).from(sportEditions).where(and(
            eq(sportEditions.competitionId, data.competitionId),
            eq(sportEditions.seasonId, seasonId),
        )).limit(1);
        if (existing) return { id: existing.id, seasonId };
        const id = newId();
        await db.insert(sportEditions).values({
            id,
            competitionId: data.competitionId,
            seasonId,
            createdAt: now,
            updatedAt: now,
        });
        return { id, seasonId };
    });

async function deletePhasesOfEditions(editionIds: string[]) {
    if (!editionIds.length) return;
    const db = getDb();
    const phases = await db.select({ id: sportPhases.id }).from(sportPhases).where(inArray(sportPhases.editionId, editionIds));
    const phaseIds = phases.map((p) => p.id);
    if (!phaseIds.length) return;
    const fixtures = await db.select({ id: sportFixtures.id }).from(sportFixtures).where(inArray(sportFixtures.phaseId, phaseIds));
    const fixtureIds = fixtures.map((f) => f.id);
    if (fixtureIds.length) {
        await db.delete(sportMatchEvents).where(inArray(sportMatchEvents.fixtureId, fixtureIds));
        await db.delete(sportMatchLineups).where(inArray(sportMatchLineups.fixtureId, fixtureIds));
    }
    await db.delete(sportFixtures).where(inArray(sportFixtures.phaseId, phaseIds));
    await db.delete(sportTies).where(inArray(sportTies.phaseId, phaseIds));
    await db.delete(sportStandings).where(inArray(sportStandings.phaseId, phaseIds));
    await db.delete(sportPhaseParticipations).where(inArray(sportPhaseParticipations.phaseId, phaseIds));
    await db.delete(sportPhaseGroups).where(inArray(sportPhaseGroups.phaseId, phaseIds));
    await db.delete(sportPhases).where(inArray(sportPhases.id, phaseIds));
}

export const adminDeleteEditionFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await deletePhasesOfEditions([data.id]);
        await getDb().delete(sportEditions).where(eq(sportEditions.id, data.id));
        return { ok: true };
    });

export const adminDeleteSeasonFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const editions = await db.select({ id: sportEditions.id }).from(sportEditions).where(eq(sportEditions.seasonId, data.id));
        await deletePhasesOfEditions(editions.map((e) => e.id));
        await db.delete(sportEditions).where(eq(sportEditions.seasonId, data.id));
        await db.delete(sportSquadMemberships).where(eq(sportSquadMemberships.seasonId, data.id));
        await db.delete(sportPlayerSeasonStats).where(eq(sportPlayerSeasonStats.seasonId, data.id));
        await db.delete(sportSeasons).where(eq(sportSeasons.id, data.id));
        return { ok: true };
    });

export const adminSeasonWorkspaceFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [edition] = await db.select().from(sportEditions).where(eq(sportEditions.id, data.id)).limit(1);
        if (!edition) return null;
        const [season] = await db.select().from(sportSeasons).where(eq(sportSeasons.id, edition.seasonId)).limit(1);
        if (!season) return null;
        const [competition] = await db.select().from(sportCompetitions).where(eq(sportCompetitions.id, edition.competitionId)).limit(1);
        const phases = await db.select().from(sportPhases).where(eq(sportPhases.editionId, edition.id)).orderBy(asc(sportPhases.sortOrder));
        const phaseIds = phases.map((p) => p.id);
        const [groups, participations, fixtures, ties, standings, memberships] = await Promise.all([
            phaseIds.length
                ? db.select().from(sportPhaseGroups).where(inArray(sportPhaseGroups.phaseId, phaseIds))
                : Promise.resolve([]),
            phaseIds.length
                ? db.select().from(sportPhaseParticipations).where(inArray(sportPhaseParticipations.phaseId, phaseIds))
                : Promise.resolve([]),
            phaseIds.length
                ? db.select().from(sportFixtures).where(inArray(sportFixtures.phaseId, phaseIds)).orderBy(asc(sportFixtures.round), asc(sportFixtures.kickoffAt))
                : Promise.resolve([]),
            phaseIds.length
                ? db.select().from(sportTies).where(inArray(sportTies.phaseId, phaseIds)).orderBy(asc(sportTies.round), asc(sportTies.sortOrder))
                : Promise.resolve([]),
            phaseIds.length
                ? db.select().from(sportStandings).where(inArray(sportStandings.phaseId, phaseIds)).orderBy(asc(sportStandings.rank))
                : Promise.resolve([]),
            db.select().from(sportSquadMemberships).where(eq(sportSquadMemberships.seasonId, season.id)),
        ]);
        const fixtureIds = fixtures.map((f) => f.id);
        const events = fixtureIds.length
            ? await db.select().from(sportMatchEvents).where(inArray(sportMatchEvents.fixtureId, fixtureIds)).orderBy(asc(sportMatchEvents.sortOrder))
            : [];
        const teamIds = uniqueIds([
            ...participations.map((p) => p.teamId),
            ...fixtures.flatMap((f) => [f.homeTeamId, f.awayTeamId]),
            ...ties.flatMap((t) => [t.homeTeamId, t.awayTeamId, t.winnerTeamId]),
            ...memberships.map((m) => m.teamId),
            ...standings.map((s) => s.teamId),
        ]);
        const teams = teamIds.length
            ? await db.select().from(sportTeams).where(inArray(sportTeams.id, teamIds)).orderBy(asc(sportTeams.name))
            : [];
        const clubIds = uniqueIds(teams.map((t) => t.clubId));
        const personIds = uniqueIds([
            ...memberships.map((m) => m.personId),
            ...events.map((e) => e.personId),
        ]);
        const venueIds = uniqueIds(fixtures.map((f) => f.venueId));
        const [clubs, people, venues] = await Promise.all([
            clubIds.length
                ? db.select().from(sportClubs).where(inArray(sportClubs.id, clubIds)).orderBy(asc(sportClubs.name))
                : Promise.resolve([]),
            personIds.length
                ? db.select().from(sportPeople).where(inArray(sportPeople.id, personIds)).orderBy(asc(sportPeople.lastName))
                : Promise.resolve([]),
            venueIds.length
                ? db.select().from(sportVenues).where(inArray(sportVenues.id, venueIds)).orderBy(asc(sportVenues.name))
                : Promise.resolve([]),
        ]);
        return {
            edition,
            season,
            competition,
            phases,
            groups,
            participations,
            fixtures,
            ties,
            teams,
            clubs,
            standings,
            people,
            memberships,
            events,
            venues,
        };
    });

export const adminUpsertPhaseFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        seasonId: z.string().min(1),
        name: z.string().min(1),
        kind: z.enum(PHASE_KINDS),
        sortOrder: z.number().int().optional(),
        rules: phaseRulesSchema.optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const [edition] = await db.select().from(sportEditions).where(eq(sportEditions.id, data.seasonId)).limit(1);
        const [competition] = edition
            ? await db.select().from(sportCompetitions).where(eq(sportCompetitions.id, edition.competitionId)).limit(1)
            : [];
        const template = competition?.defaultRules ?? rulesForPhaseKind(data.kind);
        const rules = data.rules
            ? parsePhaseRules(data.rules)
            : mergePhaseRules(data.kind, template);
        if (data.kind === 'knockout' && !rules.knockout) {
            rules.knockout = rulesForPhaseKind('knockout').knockout;
        }
        const values = {
            id,
            editionId: data.seasonId,
            name: data.name.trim(),
            kind: data.kind,
            sortOrder: data.sortOrder ?? 0,
            rules,
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportPhases.id }).from(sportPhases).where(eq(sportPhases.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportPhases).set(values).where(eq(sportPhases.id, id));
        else {
            if (data.sortOrder === undefined) {
                const [maxRow] = await db
                    .select({ value: sql<number>`coalesce(max(${sportPhases.sortOrder}), -1)` })
                    .from(sportPhases)
                    .where(eq(sportPhases.editionId, data.seasonId));
                values.sortOrder = Number(maxRow?.value ?? -1) + 1;
            }
            await db.insert(sportPhases).values({ ...values, createdAt: now });
        }
        await rebuildPhaseStandings(id);
        return { id };
    });

export const adminDeletePhaseFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const fixtures = await db.select({ id: sportFixtures.id }).from(sportFixtures).where(eq(sportFixtures.phaseId, data.id));
        const fixtureIds = fixtures.map((f) => f.id);
        if (fixtureIds.length) {
            await db.delete(sportMatchEvents).where(inArray(sportMatchEvents.fixtureId, fixtureIds));
            await db.delete(sportMatchLineups).where(inArray(sportMatchLineups.fixtureId, fixtureIds));
        }
        await db.delete(sportFixtures).where(eq(sportFixtures.phaseId, data.id));
        await db.delete(sportTies).where(eq(sportTies.phaseId, data.id));
        await db.delete(sportStandings).where(eq(sportStandings.phaseId, data.id));
        await db.delete(sportPhaseParticipations).where(eq(sportPhaseParticipations.phaseId, data.id));
        await db.delete(sportPhaseGroups).where(eq(sportPhaseGroups.phaseId, data.id));
        await db.delete(sportPhases).where(eq(sportPhases.id, data.id));
        return { ok: true };
    });

export const adminUpsertPhaseGroupFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        phaseId: z.string().min(1),
        name: z.string().min(1),
        sortOrder: z.number().int().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const values = {
            id,
            phaseId: data.phaseId,
            name: data.name.trim(),
            sortOrder: data.sortOrder ?? 0,
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportPhaseGroups.id }).from(sportPhaseGroups).where(eq(sportPhaseGroups.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportPhaseGroups).set(values).where(eq(sportPhaseGroups.id, id));
        else {
            if (data.sortOrder === undefined) {
                const [maxRow] = await db
                    .select({ value: sql<number>`coalesce(max(${sportPhaseGroups.sortOrder}), -1)` })
                    .from(sportPhaseGroups)
                    .where(eq(sportPhaseGroups.phaseId, data.phaseId));
                values.sortOrder = Number(maxRow?.value ?? -1) + 1;
            }
            await db.insert(sportPhaseGroups).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminDeletePhaseGroupFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        await db.update(sportPhaseParticipations).set({ groupId: null, updatedAt: new Date() }).where(eq(sportPhaseParticipations.groupId, data.id));
        await db.delete(sportPhaseGroups).where(eq(sportPhaseGroups.id, data.id));
        return { ok: true };
    });

export const adminEnrollTeamFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        phaseId: z.string().min(1),
        clubId: z.string().min(1).optional(),
        teamId: z.string().min(1).optional(),
        startingPoints: z.number().int().optional(),
        deductionPoints: z.number().int().optional(),
        groupId: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        let team;
        if (data.teamId) {
            const [found] = await db.select().from(sportTeams).where(eq(sportTeams.id, data.teamId)).limit(1);
            if (!found) throw new Error('קבוצה לא נמצאה');
            if (data.clubId && found.clubId !== data.clubId) throw new Error('הקבוצה אינה שייכת למועדון שנבחר');
            team = found;
        } else {
            if (!data.clubId) throw new Error('בחרו מועדון או קבוצה');
            const [club] = await db.select().from(sportClubs).where(eq(sportClubs.id, data.clubId)).limit(1);
            if (!club) throw new Error('מועדון לא נמצא');
            team = await ensureClubFirstTeam(club.id, club.name);
        }
        const [existing] = await db
            .select()
            .from(sportPhaseParticipations)
            .where(and(eq(sportPhaseParticipations.phaseId, data.phaseId), eq(sportPhaseParticipations.teamId, team.id)))
            .limit(1);
        if (existing) return { id: existing.id };
        const now = new Date();
        const id = newId();
        await db.insert(sportPhaseParticipations).values({
            id,
            phaseId: data.phaseId,
            teamId: team.id,
            groupId: emptyToNull(data.groupId),
            startingPoints: data.startingPoints ?? 0,
            deductionPoints: data.deductionPoints ?? 0,
            createdAt: now,
            updatedAt: now,
        });
        await rebuildPhaseStandings(data.phaseId);
        return { id };
    });

export const adminUpdateParticipationFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().min(1),
        startingPoints: z.number().int().optional(),
        deductionPoints: z.number().int().optional(),
        groupId: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db.select().from(sportPhaseParticipations).where(eq(sportPhaseParticipations.id, data.id)).limit(1);
        if (!row) throw new Error('השתתפות לא נמצאה');
        await db.update(sportPhaseParticipations).set({
            startingPoints: data.startingPoints ?? row.startingPoints,
            deductionPoints: data.deductionPoints ?? row.deductionPoints,
            groupId: data.groupId === undefined ? row.groupId : emptyToNull(data.groupId),
            updatedAt: new Date(),
        }).where(eq(sportPhaseParticipations.id, data.id));
        await rebuildPhaseStandings(row.phaseId);
        return { ok: true };
    });

export const adminRemoveParticipationFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db.select().from(sportPhaseParticipations).where(eq(sportPhaseParticipations.id, data.id)).limit(1);
        if (!row) return { ok: true };
        await db.delete(sportPhaseParticipations).where(eq(sportPhaseParticipations.id, data.id));
        await rebuildPhaseStandings(row.phaseId);
        return { ok: true };
    });

export const adminGenerateFixturesFn = createServerFn({ method: 'POST' })
    .validator(z.object({ phaseId: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [phase] = await db.select().from(sportPhases).where(eq(sportPhases.id, data.phaseId)).limit(1);
        if (!phase) throw new Error('שלב לא נמצא');
        const rules = parsePhaseRules(phase.rules);
        const parts = await db.select().from(sportPhaseParticipations).where(eq(sportPhaseParticipations.phaseId, data.phaseId));
        const teamIds = parts.map((p) => p.teamId);
        const all = generateHomeAwayFixtures(teamIds);
        const halfRounds = all.reduce((max, m) => Math.max(max, m.round), 0) / 2;
        const generated = rules.homeAndAway
            ? all
            : all.filter((m) => m.round <= halfRounds);
        const now = new Date();
        const rows = generated.map((m) => ({
            id: newId(),
            phaseId: data.phaseId,
            round: m.round,
            homeTeamId: m.homeTeamId,
            awayTeamId: m.awayTeamId,
            status: 'scheduled' as const,
            coverageMode: rules.coverageDefault,
            createdAt: now,
            updatedAt: now,
        }));
        if (rows.length) await db.insert(sportFixtures).values(rows);
        return { count: rows.length };
    });

export const adminUpsertFixtureFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        phaseId: z.string().min(1),
        groupId: z.string().nullable().optional(),
        tieId: z.string().nullable().optional(),
        round: z.number().int().optional(),
        kickoffAt: z.string().nullable().optional(),
        venueId: z.string().nullable().optional(),
        homeTeamId: z.string().min(1),
        awayTeamId: z.string().min(1),
        status: z.enum(['scheduled', 'live', 'played', 'postponed', 'awarded', 'abandoned']).optional(),
        coverageMode: z.enum(['off', 'live']).optional(),
        homeGoals: z.number().int().nullable().optional(),
        awayGoals: z.number().int().nullable().optional(),
        homeExtraTimeGoals: z.number().int().nullable().optional(),
        awayExtraTimeGoals: z.number().int().nullable().optional(),
        homePenalties: z.number().int().nullable().optional(),
        awayPenalties: z.number().int().nullable().optional(),
        attendance: z.number().int().nullable().optional(),
        minute: z.number().int().nullable().optional(),
        period: z.string().nullable().optional(),
        finishLive: z.boolean().optional(),
        events: z.array(z.object({
            id: z.string().optional(),
            minute: z.number().int().nullable().optional(),
            extraMinute: z.number().int().nullable().optional(),
            type: z.enum(MATCH_EVENT_TYPES),
            personId: z.string().nullable().optional(),
            assistPersonId: z.string().nullable().optional(),
            teamId: z.string().min(1),
        })).optional(),
        lineups: z.array(z.object({
            teamId: z.string().min(1),
            personId: z.string().min(1),
            started: z.boolean(),
            position: z.string().nullable().optional(),
            sortOrder: z.number().int().optional(),
        })).optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const [phase] = await db.select().from(sportPhases).where(eq(sportPhases.id, data.phaseId)).limit(1);
        const rules = parsePhaseRules(phase?.rules);
        const coverageMode = data.coverageMode ?? rules.coverageDefault;
        let homeGoals = data.homeGoals ?? null;
        let awayGoals = data.awayGoals ?? null;
        if (data.events) {
            const computed = scoreFromEvents(
                data.events.map((e) => ({ type: e.type, teamId: e.teamId })),
                data.homeTeamId,
                data.awayTeamId,
            );
            const scored = data.events.some((e) => isScoringEvent(e.type));
            const recordScore = scored
                || data.status === 'played'
                || data.status === 'live'
                || data.finishLive;
            homeGoals = recordScore ? computed.homeGoals : null;
            awayGoals = recordScore ? computed.awayGoals : null;
        }
        const hasFinalScore = homeGoals != null && awayGoals != null;
        let status = data.status ?? 'scheduled';
        if (data.finishLive) status = 'played';
        else status = statusAfterSave({ coverageMode, status, hasFinalScore });

        const values = {
            id,
            phaseId: data.phaseId,
            groupId: emptyToNull(data.groupId),
            tieId: emptyToNull(data.tieId),
            round: data.round ?? 1,
            kickoffAt: data.kickoffAt ? new Date(data.kickoffAt) : null,
            venueId: emptyToNull(data.venueId),
            homeTeamId: data.homeTeamId,
            awayTeamId: data.awayTeamId,
            status,
            coverageMode,
            homeGoals,
            awayGoals,
            homeExtraTimeGoals: data.homeExtraTimeGoals ?? null,
            awayExtraTimeGoals: data.awayExtraTimeGoals ?? null,
            homePenalties: data.homePenalties ?? null,
            awayPenalties: data.awayPenalties ?? null,
            attendance: data.attendance ?? null,
            minute: data.minute ?? null,
            period: emptyToNull(data.period),
            updatedAt: now,
        };

        const [existing] = data.id
            ? await db.select().from(sportFixtures).where(eq(sportFixtures.id, id)).limit(1)
            : [];
        const coverageStartedAt = status === 'live'
            ? (existing?.coverageStartedAt ?? now)
            : existing?.coverageStartedAt ?? null;
        const coverageEndedAt = status === 'played' || status === 'awarded'
            ? (existing?.coverageEndedAt ?? now)
            : null;
        const persistValues = { ...values, coverageStartedAt, coverageEndedAt };
        if (existing) await db.update(sportFixtures).set(persistValues).where(eq(sportFixtures.id, id));
        else await db.insert(sportFixtures).values({ ...persistValues, createdAt: now });

        if (data.events) {
            await db.delete(sportMatchEvents).where(eq(sportMatchEvents.fixtureId, id));
            if (data.events.length) {
                await db.insert(sportMatchEvents).values(
                    data.events.map((event, index) => ({
                        id: event.id || newId(),
                        fixtureId: id,
                        minute: event.minute ?? null,
                        extraMinute: event.extraMinute ?? null,
                        type: event.type,
                        personId: emptyToNull(event.personId),
                        assistPersonId: emptyToNull(event.assistPersonId),
                        teamId: event.teamId,
                        sortOrder: index,
                        createdAt: now,
                        updatedAt: now,
                    })),
                );
            }
        }

        if (data.lineups) {
            await db.delete(sportMatchLineups).where(eq(sportMatchLineups.fixtureId, id));
            if (data.lineups.length) {
                await db.insert(sportMatchLineups).values(
                    data.lineups.map((row, index) => ({
                        id: newId(),
                        fixtureId: id,
                        teamId: row.teamId,
                        personId: row.personId,
                        started: row.started,
                        position: emptyToNull(row.position),
                        sortOrder: row.sortOrder ?? index,
                        createdAt: now,
                        updatedAt: now,
                    })),
                );
            }
        }

        await rebuildAfterFixture(id);
        return { id };
    });

export const adminGetFixtureFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [fixture] = await db.select().from(sportFixtures).where(eq(sportFixtures.id, data.id)).limit(1);
        if (!fixture) return null;
        const [phase] = await db.select().from(sportPhases).where(eq(sportPhases.id, fixture.phaseId)).limit(1);
        const [edition] = phase
            ? await db.select().from(sportEditions).where(eq(sportEditions.id, phase.editionId)).limit(1)
            : [];
        const [season] = edition
            ? await db.select().from(sportSeasons).where(eq(sportSeasons.id, edition.seasonId)).limit(1)
            : [];
        const [competition] = edition
            ? await db.select().from(sportCompetitions).where(eq(sportCompetitions.id, edition.competitionId)).limit(1)
            : [];
        const events = await db
            .select()
            .from(sportMatchEvents)
            .where(eq(sportMatchEvents.fixtureId, fixture.id))
            .orderBy(asc(sportMatchEvents.sortOrder), asc(sportMatchEvents.minute));
        const lineups = await db
            .select()
            .from(sportMatchLineups)
            .where(eq(sportMatchLineups.fixtureId, fixture.id))
            .orderBy(asc(sportMatchLineups.sortOrder));
        const teams = await db.select().from(sportTeams).where(inArray(sportTeams.id, [fixture.homeTeamId, fixture.awayTeamId]));
        const clubIds = uniqueIds(teams.map((t) => t.clubId));
        const clubs = clubIds.length
            ? await db.select().from(sportClubs).where(inArray(sportClubs.id, clubIds))
            : [];
        const memberships = season
            ? await db.select().from(sportSquadMemberships).where(and(
                inArray(sportSquadMemberships.teamId, [fixture.homeTeamId, fixture.awayTeamId]),
                eq(sportSquadMemberships.seasonId, season.id),
            ))
            : [];
        const personIds = uniqueIds([
            ...events.map((e) => e.personId),
            ...events.map((e) => e.assistPersonId),
            ...lineups.map((l) => l.personId),
            ...memberships.map((m) => m.personId),
        ]);
        const people = personIds.length
            ? await db.select().from(sportPeople).where(inArray(sportPeople.id, personIds))
            : [];
        return {
            fixture,
            events,
            lineups,
            memberships,
            people,
            phase: phase ?? null,
            edition: edition ?? null,
            season: season ?? null,
            competition: competition ?? null,
            teams,
            clubs,
        };
    });

export const adminDeleteFixtureFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db.select().from(sportFixtures).where(eq(sportFixtures.id, data.id)).limit(1);
        await db.delete(sportMatchEvents).where(eq(sportMatchEvents.fixtureId, data.id));
        await db.delete(sportMatchLineups).where(eq(sportMatchLineups.fixtureId, data.id));
        await db.delete(sportFixtures).where(eq(sportFixtures.id, data.id));
        if (row) await rebuildPhaseStandings(row.phaseId);
        return { ok: true };
    });

export const adminUpsertTieFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        phaseId: z.string().min(1),
        round: z.number().int().optional(),
        name: z.string().nullable().optional(),
        homeTeamId: z.string().min(1),
        awayTeamId: z.string().min(1),
        createLegs: z.boolean().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const [phase] = await db.select().from(sportPhases).where(eq(sportPhases.id, data.phaseId)).limit(1);
        const rules = parsePhaseRules(phase?.rules);
        const values = {
            id,
            phaseId: data.phaseId,
            round: data.round ?? 1,
            name: emptyToNull(data.name),
            homeTeamId: data.homeTeamId,
            awayTeamId: data.awayTeamId,
            sortOrder: 0,
            updatedAt: now,
        };
        const [existing] = data.id
            ? await db.select({ id: sportTies.id }).from(sportTies).where(eq(sportTies.id, id)).limit(1)
            : [];
        if (existing) await db.update(sportTies).set(values).where(eq(sportTies.id, id));
        else await db.insert(sportTies).values({ ...values, createdAt: now });

        if (data.createLegs && !data.id) {
            const legs = rules.knockout?.legs ?? 1;
            const coverageMode = rules.coverageDefault;
            const first = {
                id: newId(),
                phaseId: data.phaseId,
                tieId: id,
                round: data.round ?? 1,
                homeTeamId: legs === 2 ? data.awayTeamId : data.homeTeamId,
                awayTeamId: legs === 2 ? data.homeTeamId : data.awayTeamId,
                status: 'scheduled' as const,
                coverageMode,
                createdAt: now,
                updatedAt: now,
            };
            await db.insert(sportFixtures).values(first);
            if (legs === 2) {
                await db.insert(sportFixtures).values({
                    id: newId(),
                    phaseId: data.phaseId,
                    tieId: id,
                    round: data.round ?? 1,
                    homeTeamId: data.homeTeamId,
                    awayTeamId: data.awayTeamId,
                    status: 'scheduled',
                    coverageMode,
                    createdAt: now,
                    updatedAt: now,
                });
            }
        }
        return { id };
    });

export const adminGetLatestActiveSeasonFn = createServerFn({ method: 'GET' }).handler(async () => {
    await requireAdminAccess();
    const db = getDb();
    const columns = {
        id: sportSeasons.id,
        label: sportSeasons.label,
    };
    const [active] = await db
        .select(columns)
        .from(sportSeasons)
        .where(eq(sportSeasons.status, 'active'))
        .orderBy(desc(sportSeasons.startsOn), desc(sportSeasons.label))
        .limit(1);
    if (active) return active;
    const [fallback] = await db
        .select(columns)
        .from(sportSeasons)
        .orderBy(desc(sportSeasons.startsOn), desc(sportSeasons.label))
        .limit(1);
    return fallback ?? null;
});

export const adminUpsertSquadMemberFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        id: z.string().optional(),
        teamId: z.string().min(1),
        seasonId: z.string().min(1),
        personId: z.string().min(1),
        shirtNumber: z.number().int().nullable().optional(),
        onLoan: z.boolean().optional(),
        joinedOn: z.string().nullable().optional(),
        leftOn: z.string().nullable().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const [existing] = data.id
            ? await db.select().from(sportSquadMemberships).where(eq(sportSquadMemberships.id, data.id)).limit(1)
            : await db.select().from(sportSquadMemberships).where(and(
                eq(sportSquadMemberships.teamId, data.teamId),
                eq(sportSquadMemberships.seasonId, data.seasonId),
                eq(sportSquadMemberships.personId, data.personId),
            )).limit(1);
        const id = existing?.id || data.id?.trim() || newId();
        const values = {
            id,
            teamId: data.teamId,
            seasonId: data.seasonId,
            personId: data.personId,
            shirtNumber: data.shirtNumber === undefined ? (existing?.shirtNumber ?? null) : data.shirtNumber,
            onLoan: data.onLoan ?? existing?.onLoan ?? false,
            joinedOn: data.joinedOn === undefined ? (existing?.joinedOn ?? null) : emptyToNull(data.joinedOn),
            leftOn: data.leftOn === undefined ? (existing?.leftOn ?? null) : emptyToNull(data.leftOn),
            updatedAt: now,
        };
        if (existing) await db.update(sportSquadMemberships).set(values).where(eq(sportSquadMemberships.id, existing.id));
        else await db.insert(sportSquadMemberships).values({ ...values, createdAt: now });
        return { id };
    });

export const adminDeleteSquadMemberFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(sportSquadMemberships).where(eq(sportSquadMemberships.id, data.id));
        return { ok: true };
    });

export const adminSetArticleSportRefsFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        articleId: z.string().min(1),
        refs: z.array(z.object({
            kind: z.enum(['fixture', 'club', 'person', 'competition', 'season']),
            refId: z.string().min(1),
        })),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        await db.delete(sportArticleRefs).where(eq(sportArticleRefs.articleId, data.articleId));
        if (data.refs.length) {
            await db.insert(sportArticleRefs).values(
                data.refs.map((ref) => ({
                    id: newId(),
                    articleId: data.articleId,
                    kind: ref.kind,
                    refId: ref.refId,
                    createdAt: new Date(),
                })),
            );
        }
        return { ok: true };
    });

export const adminEnqueueSportRebuildFn = createServerFn({ method: 'POST' })
    .validator(z.object({
        job: z.enum(['rebuild', 'sync']).default('rebuild'),
        seasonId: z.string().optional(),
        phaseId: z.string().optional(),
    }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const functionId = data.job === 'sync' ? 'sport-sync-results' : 'sport-rebuild-aggregates';
        try {
            const { client } = createAdminClient();
            const functions = new Functions(client);
            await functions.createExecution({
                functionId,
                body: JSON.stringify({ seasonId: data.seasonId, phaseId: data.phaseId }),
                async: true,
            });
            return { queued: true, inline: false };
        } catch {
            if (data.job === 'rebuild') {
                if (data.phaseId) await rebuildPhaseStandings(data.phaseId);
                else await rebuildAllActiveAggregates();
                return { queued: false, inline: true };
            }
            throw new Error('לא ניתן להפעיל את פונקציית הסנכרון. בדקו את Appwrite Functions.');
        }
    });

export const loadFootballHubFn = createServerFn({ method: 'GET' }).handler(async () => {
    const db = getDb();
    const competitions = await db.select().from(sportCompetitions).orderBy(desc(sportCompetitions.featured), asc(sportCompetitions.name));
    const editions = await db
        .select({
            id: sportEditions.id,
            competitionId: sportEditions.competitionId,
            seasonId: sportSeasons.id,
            label: sportSeasons.label,
            slug: sportSeasons.slug,
            status: sportSeasons.status,
        })
        .from(sportEditions)
        .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
        .orderBy(desc(sportSeasons.label));
    const featured = competitions.find((c) => c.featured) ?? competitions[0];
    const featuredSeason = featured
        ? editions.find((s) => s.competitionId === featured.id && s.status === 'active')
            ?? editions.find((s) => s.competitionId === featured.id)
        : undefined;
    let table: typeof sportStandings.$inferSelect[] = [];
    let phaseId: string | null = null;
    let fixtures: typeof sportFixtures.$inferSelect[] = [];
    if (featuredSeason) {
        const phases = await db.select().from(sportPhases).where(eq(sportPhases.editionId, featuredSeason.id)).orderBy(asc(sportPhases.sortOrder));
        const leaguePhase = phases.find((p) => p.kind === 'league' || p.kind === 'split') ?? phases[0];
        if (leaguePhase) {
            phaseId = leaguePhase.id;
            table = await db.select().from(sportStandings).where(eq(sportStandings.phaseId, leaguePhase.id)).orderBy(asc(sportStandings.rank));
            fixtures = await db.select().from(sportFixtures).where(eq(sportFixtures.phaseId, leaguePhase.id)).orderBy(asc(sportFixtures.kickoffAt)).limit(8);
        }
    }
    const [teams, clubs] = await Promise.all([
        db.select().from(sportTeams),
        db.select().from(sportClubs),
    ]);
    return {
        competitions,
        seasons: editions,
        featured,
        featuredSeason,
        phaseId,
        table,
        fixtures,
        teams,
        clubs,
    };
});

export const loadFootballCompetitionFn = createServerFn({ method: 'GET' })
    .validator(z.object({ competition: z.string(), season: z.string() }))
    .handler(async ({ data }) => {
        const db = getDb();
        const [competition] = await db.select().from(sportCompetitions).where(eq(sportCompetitions.slug, data.competition)).limit(1);
        if (!competition) return null;
        const [edition] = await db
            .select({
                id: sportEditions.id,
                competitionId: sportEditions.competitionId,
                seasonId: sportSeasons.id,
                label: sportSeasons.label,
                slug: sportSeasons.slug,
                status: sportSeasons.status,
            })
            .from(sportEditions)
            .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id))
            .where(and(eq(sportEditions.competitionId, competition.id), eq(sportSeasons.slug, data.season)))
            .limit(1);
        if (!edition) return null;
        const phases = await db.select().from(sportPhases).where(eq(sportPhases.editionId, edition.id)).orderBy(asc(sportPhases.sortOrder));
        const phaseIds = phases.map((p) => p.id);
        const [standings, fixtures, ties, teams, clubs, participations] = await Promise.all([
            phaseIds.length ? db.select().from(sportStandings).where(inArray(sportStandings.phaseId, phaseIds)).orderBy(asc(sportStandings.rank)) : Promise.resolve([]),
            phaseIds.length ? db.select().from(sportFixtures).where(inArray(sportFixtures.phaseId, phaseIds)).orderBy(asc(sportFixtures.round)) : Promise.resolve([]),
            phaseIds.length ? db.select().from(sportTies).where(inArray(sportTies.phaseId, phaseIds)) : Promise.resolve([]),
            db.select().from(sportTeams),
            db.select().from(sportClubs),
            phaseIds.length ? db.select().from(sportPhaseParticipations).where(inArray(sportPhaseParticipations.phaseId, phaseIds)) : Promise.resolve([]),
        ]);
        return { competition, season: edition, phases, standings, fixtures, ties, teams, clubs, participations };
    });

export const loadFootballClubFn = createServerFn({ method: 'GET' })
    .validator(z.object({ slug: z.string() }))
    .handler(async ({ data }) => {
        const db = getDb();
        const [club] = await db.select().from(sportClubs).where(eq(sportClubs.slug, data.slug)).limit(1);
        if (!club) return null;
        const teams = await db.select().from(sportTeams).where(eq(sportTeams.clubId, club.id));
        const teamIds = teams.map((t) => t.id);
        const memberships = teamIds.length
            ? await db.select().from(sportSquadMemberships).where(inArray(sportSquadMemberships.teamId, teamIds))
            : [];
        const peopleIds = [...new Set(memberships.map((m) => m.personId))];
        const people = peopleIds.length
            ? await db.select().from(sportPeople).where(inArray(sportPeople.id, peopleIds))
            : [];
        const venue = club.venueId
            ? (await db.select().from(sportVenues).where(eq(sportVenues.id, club.venueId)).limit(1))[0]
            : null;
        const participations = teamIds.length
            ? await db.select().from(sportPhaseParticipations).where(inArray(sportPhaseParticipations.teamId, teamIds))
            : [];
        const phaseIds = [...new Set(participations.map((p) => p.phaseId))];
        const phases = phaseIds.length
            ? await db.select().from(sportPhases).where(inArray(sportPhases.id, phaseIds))
            : [];
        const editionIds = [...new Set(phases.map((p) => p.editionId))];
        const editions = editionIds.length
            ? await db.select().from(sportEditions).where(inArray(sportEditions.id, editionIds))
            : [];
        const seasonIds = [...new Set(editions.map((e) => e.seasonId))];
        const seasons = seasonIds.length
            ? await db.select().from(sportSeasons).where(inArray(sportSeasons.id, seasonIds))
            : [];
        const competitionIds = [...new Set(editions.map((e) => e.competitionId))];
        const competitions = competitionIds.length
            ? await db.select().from(sportCompetitions).where(inArray(sportCompetitions.id, competitionIds))
            : [];
        const activeEditions = editions.filter((edition) => {
            const season = seasons.find((s) => s.id === edition.seasonId);
            return season?.status === 'active' || season?.status === 'upcoming';
        });
        const activeSeasonIds = new Set(activeEditions.map((e) => e.seasonId));
        const competitionsForClub = activeEditions.flatMap((edition) => {
            const competition = competitions.find((c) => c.id === edition.competitionId);
            const season = seasons.find((s) => s.id === edition.seasonId);
            if (!competition || !season) return [];
            return [{
                name: competition.name,
                slug: competition.slug,
                logoFileId: competition.logoFileId,
                seasonLabel: season.label,
                seasonSlug: season.slug,
                status: season.status,
            }];
        });
        const seenComp = new Set<string>();
        const uniqueCompetitions = competitionsForClub.filter((row) => {
            const key = `${row.slug}:${row.seasonSlug}`;
            if (seenComp.has(key)) return false;
            seenComp.add(key);
            return true;
        });
        const fixtureWhere = teamIds.length
            ? or(inArray(sportFixtures.homeTeamId, teamIds), inArray(sportFixtures.awayTeamId, teamIds))
            : undefined;
        const [upcoming, recent] = fixtureWhere
            ? await Promise.all([
                db.select().from(sportFixtures).where(and(
                    fixtureWhere,
                    inArray(sportFixtures.status, ['scheduled', 'live']),
                )).orderBy(asc(sportFixtures.kickoffAt)).limit(8),
                db.select().from(sportFixtures).where(and(
                    fixtureWhere,
                    inArray(sportFixtures.status, ['played', 'awarded']),
                )).orderBy(desc(sportFixtures.kickoffAt)).limit(8),
            ])
            : [[], []];
        const fixtureTeamIds = [...new Set([
            ...upcoming.flatMap((f) => [f.homeTeamId, f.awayTeamId]),
            ...recent.flatMap((f) => [f.homeTeamId, f.awayTeamId]),
            ...teamIds,
        ])];
        const allTeams = fixtureTeamIds.length
            ? await db.select().from(sportTeams).where(inArray(sportTeams.id, fixtureTeamIds))
            : teams;
        const allClubIds = [...new Set(allTeams.map((t) => t.clubId))];
        const clubs = allClubIds.length
            ? await db.select().from(sportClubs).where(inArray(sportClubs.id, allClubIds))
            : [club];
        const activeMemberships = memberships.filter((m) =>
            activeSeasonIds.size === 0 || activeSeasonIds.has(m.seasonId),
        );
        return {
            club,
            teams,
            memberships: activeMemberships.length ? activeMemberships : memberships,
            people,
            upcoming,
            recent,
            fixtures: [...upcoming, ...recent],
            venue,
            allTeams,
            clubs,
            competitions: uniqueCompetitions,
        };
    });

export const loadFootballPlayerFn = createServerFn({ method: 'GET' })
    .validator(z.object({ slug: z.string() }))
    .handler(async ({ data }) => {
        const db = getDb();
        const [person] = await db.select().from(sportPeople).where(eq(sportPeople.slug, data.slug)).limit(1);
        if (!person) return null;
        const [memberships, stats] = await Promise.all([
            db.select().from(sportSquadMemberships).where(eq(sportSquadMemberships.personId, person.id)),
            db.select().from(sportPlayerSeasonStats).where(eq(sportPlayerSeasonStats.personId, person.id)),
        ]);
        const clubs = await db.select().from(sportClubs);
        const teams = await db.select().from(sportTeams);
        const seasons = await db.select().from(sportSeasons);
        return { person, memberships, stats, clubs, teams, seasons, displayName: personName(person) };
    });

export const loadFootballMatchFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string() }))
    .handler(async ({ data }) => {
        const db = getDb();
        const [fixture] = await db.select().from(sportFixtures).where(eq(sportFixtures.id, data.id)).limit(1);
        if (!fixture) return null;
        const [phase] = await db.select().from(sportPhases).where(eq(sportPhases.id, fixture.phaseId)).limit(1);
        const edition = phase
            ? (await db.select().from(sportEditions).where(eq(sportEditions.id, phase.editionId)).limit(1))[0]
            : null;
        const season = edition
            ? (await db.select().from(sportSeasons).where(eq(sportSeasons.id, edition.seasonId)).limit(1))[0]
            : null;
        const competition = edition
            ? (await db.select().from(sportCompetitions).where(eq(sportCompetitions.id, edition.competitionId)).limit(1))[0]
            : null;
        const events = await db.select().from(sportMatchEvents).where(eq(sportMatchEvents.fixtureId, fixture.id)).orderBy(asc(sportMatchEvents.sortOrder), asc(sportMatchEvents.minute));
        const [teams, clubs, people, venue] = await Promise.all([
            db.select().from(sportTeams),
            db.select().from(sportClubs),
            db.select().from(sportPeople),
            fixture.venueId
                ? db.select().from(sportVenues).where(eq(sportVenues.id, fixture.venueId)).limit(1).then((r) => r[0] ?? null)
                : Promise.resolve(null),
        ]);
        const articles = await db.select().from(sportArticleRefs).where(
            and(eq(sportArticleRefs.kind, 'fixture'), eq(sportArticleRefs.refId, fixture.id)),
        );
        return { fixture, phase, season, competition, events, teams, clubs, people, venue, articles };
    });

export const loadFootballWidgetFn = createServerFn({ method: 'GET' }).handler(async () => {
    const hub = await loadFootballHubFn();
    return {
        featured: hub.featured,
        featuredSeason: hub.featuredSeason,
        table: hub.table.slice(0, 6),
        fixtures: hub.fixtures.filter((f) => f.status === 'scheduled' || f.status === 'live').slice(0, 3),
        teams: hub.teams,
        clubs: hub.clubs,
    };
});

function isoStamp(value: Date | string | null | undefined): string {
    if (!value) return new Date().toISOString();
    if (value instanceof Date) return value.toISOString();
    return new Date(value).toISOString();
}

/** Public football URLs for the sitemap (competitions, clubs, players, recent matches). */
export const loadFootballSitemapFn = createServerFn({ method: 'GET' }).handler(async () => {
    const db = getDb();
    const since = new Date(Date.now() - 120 * 24 * 60 * 60 * 1000);

    const [editionRows, clubs, players, matches] = await Promise.all([
        db
            .select({
                competitionSlug: sportCompetitions.slug,
                seasonSlug: sportSeasons.slug,
                competitionUpdatedAt: sportCompetitions.updatedAt,
                seasonUpdatedAt: sportSeasons.updatedAt,
                editionUpdatedAt: sportEditions.updatedAt,
            })
            .from(sportEditions)
            .innerJoin(sportCompetitions, eq(sportEditions.competitionId, sportCompetitions.id))
            .innerJoin(sportSeasons, eq(sportEditions.seasonId, sportSeasons.id)),
        db
            .select({ slug: sportClubs.slug, updatedAt: sportClubs.updatedAt })
            .from(sportClubs),
        db
            .select({ slug: sportPeople.slug, updatedAt: sportPeople.updatedAt })
            .from(sportPeople),
        db
            .select({ id: sportFixtures.id, updatedAt: sportFixtures.updatedAt })
            .from(sportFixtures)
            .where(
                or(
                    inArray(sportFixtures.status, ['scheduled', 'live']),
                    and(
                        inArray(sportFixtures.status, ['played', 'awarded']),
                        gte(sportFixtures.updatedAt, since),
                    ),
                ),
            )
            .orderBy(desc(sportFixtures.updatedAt))
            .limit(2000),
    ]);

    return {
        competitions: editionRows.map((row) => ({
            competitionSlug: row.competitionSlug,
            seasonSlug: row.seasonSlug,
            updatedAt: isoStamp(
                [row.editionUpdatedAt, row.competitionUpdatedAt, row.seasonUpdatedAt]
                    .filter(Boolean)
                    .sort((a, b) => +new Date(b) - +new Date(a))[0],
            ),
        })),
        clubs: clubs.map((c) => ({ slug: c.slug, updatedAt: isoStamp(c.updatedAt) })),
        players: players.map((p) => ({ slug: p.slug, updatedAt: isoStamp(p.updatedAt) })),
        matches: matches.map((m) => ({ id: m.id, updatedAt: isoStamp(m.updatedAt) })),
    };
});
